// @ts-nocheck
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_URL } from '../constants/apiConstants';
import axiosInstance from '../api/axios';

// Interfaces

interface Sector {
  sectorname: string;
  sectorcode: string;
}
interface Region {
  regionid: string;
  regionname: string;
}
interface Site {
  id: number;
  sitename: string;
  sitealias?: string;
  siteid: string;
  sitecode: string;
}

interface SiteState {
  sectors: Sector[];
  regions: Region[];
  sites: Site[];
  loading: boolean;
  error: string | null;
}

const initialState: SiteState = {
  sectors: [],
  regions: [],
  sites: [],
  loading: false,
  error: null,
};

// Don't cache roleId at module load time because it can change at runtime
// when the user switches roles. Read it inside thunks to always use the
// current value stored in localStorage.

// Fetch Sectors
export const fetchSectors = createAsyncThunk(
  'AdminConfiguration/fetchSectors',
  async (userId: string | null) => {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/api/v1/AdminConfiguration/GetAllSectorByUserId`,
      { userid: userId, roleid: Number(localStorage.getItem('roleId')) }
      );
      const data = response.data;
      if (!data.success) {
        throw new Error(data.errormessage || 'Failed to fetch sectors');
      }
      return data.data.sectors;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.errormessage || 'Failed to fetch sectors'
      );
    }
  }
);

// Fetch Regions
export const fetchAllRegions = createAsyncThunk(
  'AdminConfiguration/fetchAllRegions',
  async (
    { userId, sectorCode }: { userId: string | null; sectorCode: string }
  ) => {
    try {
      if (!sectorCode) {
        return [];
      }
      const response = await axiosInstance.post(
        `${BASE_URL}/api/v1/AdminConfiguration/GetAllRegionByUserIdAndSector`,
  { userid: userId, sectorcode: sectorCode, roleid: Number(localStorage.getItem('roleId')) }
      );
      const data = response.data;
      if (!data.success) {
        throw new Error(data.errormessage || 'Failed to fetch regions');
      }
      return data.data.regions;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.errormessage || 'Failed to fetch regions'
      );
    }
  }
);

// Fetch Sites
export const fetchAllSites = createAsyncThunk(
  'AdminConfiguration/fetchAllSites',
  async (
    { userId, sector, region }: { userId: string | null; sector: string; region: string }
  ) => {
    try {
      if (!sector || !region) {
        return [];
      }
      const response = await axiosInstance.post(
        `${BASE_URL}/api/v1/AdminConfiguration/GetAllSites`,
  { userid: userId, sectorcode: sector, regionid: region, roleid: Number(localStorage.getItem('roleId')) }
      );
      const data = response.data;
      if (!data.success) {
        throw new Error(data.errormessage || 'Failed to fetch sites');
      }
      return data.data.sitelist;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.errormessage || 'Failed to fetch sites'
      );
    }
  }
);

// Slice
const sectorRegionAllSitesSlice = createSlice({
  name: 'sectorRegionAllSitesSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSectors.fulfilled, (state, action) => {
        state.sectors = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllRegions.fulfilled, (state, action) => {
        state.regions = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllSites.fulfilled, (state, action) => {
        state.sites = action.payload;
        state.loading = false;
      })
      .addCase(fetchSectors.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch sectors';
        state.loading = false;
      })
      .addCase(fetchAllRegions.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch regions';
        state.loading = false;
      })
      .addCase(fetchAllSites.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch sites';
        state.loading = false;
      })
      .addCase(fetchSectors.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllRegions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllSites.pending, (state) => {
        state.loading = true;
      });
  },
});

export default sectorRegionAllSitesSlice.reducer;
